import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../common/Button';
import { slugify } from '../../utils/slug';
import ExploreIcon from '../../../assets/Shine.svg';
import { fetchInternationalPackages } from '../../utils/api';

const PackageCard = () => {
  const [favorites, setFavorites] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const MINIMUM_LOAD_TIME = 0;

    const loadPackages = async () => {
      const startTime = Date.now();
      try {
        setLoading(true);
        const data = await fetchInternationalPackages({ type: 'category' });
        // Filter for category type and map to desired format
        const formattedPackages = data
          .filter((item) => item.type === 'category')
          .map((item) => ({
            _id: item._id,
            title: `${item.name}`,
            image: `${process.env.REACT_APP_API_BASE_URL}/${item.image}`,
          }))
          // Sort packages alphabetically by title
          .sort((a, b) => a.title.localeCompare(b.title));
        setPackages(formattedPackages);

        // Ensure minimum loading time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = MINIMUM_LOAD_TIME - elapsedTime;
        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }
      } catch (err) {
        setError('Failed to load packages. Please try again later.');
        console.error('Error fetching packages:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPackages();
  }, []);

  const toggleFavorite = (id) => {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  if (error) {
    return <div className='py-8 text-center text-red-500'>{error}</div>;
  }

  // Skeleton Loader Component
  const SkeletonCard = () => (
    <div className='relative w-full animate-pulse rounded-lg border border-[#55E6A5] bg-white p-4 shadow-md'>
      {/* Image Skeleton */}
      <div className='relative'>
        <div className='h-[220px] w-full rounded-lg bg-gray-200'></div>
        <div className='absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-gray-200'></div>
      </div>
      {/* Title Skeleton */}
      <div className='mt-2 flex flex-col items-center p-3 text-center'>
        <div className='h-6 w-3/4 rounded bg-gray-200'></div>
      </div>
      {/* Button Skeleton */}
      <div className='mt-2 h-10 w-full rounded bg-gray-200'></div>
    </div>
  );

  return (
    <div className='py-10'>
      <div className='mb-8 flex flex-col items-center gap-3 md:mb-12'>
        {/* Small Heading with Icon */}
        <div className='flex items-center gap-3'>
          <img
            src={ExploreIcon}
            alt='explore'
            className='h-14 w-14'
          />
          <p className='mont text-2xl font-bold tracking-wide text-gray-900 md:text-3xl'>
            Explore Tour
          </p>
        </div>

        <div className='flex gap-3 text-center'>
          <h3 className='mont relative text-2xl font-bold text-gray-900 md:text-4xl'>
            International Packages
          </h3>
        </div>
      </div>
      <div className='container mx-auto px-4 lg:px-16'>
        <div className='grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
          {loading
            ? Array(4)
                .fill()
                .map((_, index) => <SkeletonCard key={index} />)
            : packages.map((pkg) => (
                <div
                  key={pkg._id}
                  className='relative w-full rounded-lg border border-[#55E6A5] bg-white p-4 shadow-md'
                >
                  {/* Image with heart icon */}
                  <div className='relative'>
                    <Link
                      to={`/international/${slugify(pkg.title)}`}
                      className='block overflow-hidden rounded-lg'
                    >
                      <img
                        src={pkg.image}
                        alt={pkg.title}
                        className='h-[220px] w-full object-cover transition-transform duration-500 ease-in-out hover:scale-110'
                      />
                    </Link>

                    <button
                      onClick={() => toggleFavorite(pkg._id)}
                      aria-label={`Favorite ${pkg.title}`}
                      className={`absolute -bottom-3 right-2 flex h-8 w-8 items-center justify-center rounded-full border border-[#55E6A5] shadow-md transition ${
                        favorites.includes(pkg._id)
                          ? 'bg-[#495BFF] text-white'
                          : 'bg-white text-[#495BFF]'
                      }`}
                    >
                      <i
                        className={`${
                          favorites.includes(pkg._id) ? 'fas fa-heart' : 'far fa-heart'
                        } text-md`}
                      ></i>
                    </button>
                  </div>

                  {/* Title with location icon */}
                  <Link to={`/international/${slugify(pkg.title)}`}>
                    <div className='mt-2 flex flex-col items-center p-3 text-center'>
                      <h3 className='flex items-start gap-1 font-semibold text-[#495BFF] transition-colors duration-300 ease-in-out hover:text-[#55E6A5]'>
                        <i className='fas fa-map-marker-alt mt-1 text-[#FF0000]'></i>
                        {pkg.title}
                      </h3>
                    </div>
                  </Link>

                  {/* Book Button */}
                  <div className='flex justify-center'>
                    <Button category={pkg} />
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

export default PackageCard;
